import {
  AlertTriangle,
  Check,
  X,
} from "lucide-react";
import {
  formatEvaluationAmount,
} from "../../model";
import type { ApprovalRowContext } from "./approval-row-model";

export function ApprovalOverrideForm({ context }: { context: ApprovalRowContext ;}) {
  const {
    request,
    decisionError,
    setDecisionError,
    decisionSubmitting,
    setGmFinalDecisionNotes,
    gmOverrideRequestId,
    closeGmOverrideComposer,
    act,
    evaluation,
    selectedOffer,
    gmFinalOffer,
    gmChangedManagerSelection,
    gmFinalDecisionNote,
  } = context;
  return (<>
    {gmOverrideRequestId === request.id &&
      gmChangedManagerSelection &&
      gmFinalOffer ? (
      <div
        className="approval-revision-layer"
        onMouseDown={(event) => {
          if (
            !decisionSubmitting &&
            event.target === event.currentTarget
          ) {
            closeGmOverrideComposer();
          }
        }}
      >
        <section
          className="approval-revision-composer gm-override-composer"
          aria-labelledby={`gm-override-title-${request.id}`}
          role="dialog"
          aria-modal="true"
        >
          <header className="revision-composer-header">
            <div>
              <span className="section-kicker">
                Genel Müdür nihai kararı
              </span>
              <h3
                id={`gm-override-title-${request.id}`}
              >
                Nihai seçimi değiştirin
              </h3>
              <p>
                Müdür önerisinden farklı seçim,
                gerekçesiyle birlikte denetim
                geçmişine kaydedilir.
              </p>
            </div>
            <button
              type="button"
              className="icon-button"
              aria-label="Nihai seçim panelini kapat"
              disabled={decisionSubmitting}
              onClick={closeGmOverrideComposer}
            >
              <X size={18} />
            </button>
          </header>

          <div className="revision-composer-body">
            <div
              className="gm-final-decision-comparison"
              aria-label="Müdür önerisi ve nihai seçim karşılaştırması"
            >
              <div>
                <span>Müdür önerisi</span>
                <strong>
                  {selectedOffer?.supplier ?? "—"}
                </strong>
                <small>
                  {selectedOffer?.offerNo ?? "—"}
                </small>
              </div>
              <span
                className="gm-final-decision-arrow"
                aria-hidden="true"
              >
                →
              </span>
              <div>
                <span>Nihai seçim</span>
                <strong>
                  {gmFinalOffer.supplier}
                </strong>
                <small>{gmFinalOffer.offerNo}</small>
              </div>
            </div>

            <div className="gm-override-commercial-summary">
              <div>
                <span>Birim fiyat</span>
                <strong>
                  {formatEvaluationAmount(
                    gmFinalOffer.unitPrice,
                    gmFinalOffer.currency ??
                    evaluation.comparisonCurrency,
                  )}
                </strong>
              </div>
              <div>
                <span>Toplam</span>
                <strong>
                  {formatEvaluationAmount(
                    gmFinalOffer.comparableTotal ??
                    gmFinalOffer.enteredTotal ??
                    gmFinalOffer.amount,
                    evaluation.comparisonCurrency,
                  )}
                </strong>
              </div>
              <div>
                <span>Teslimat</span>
                <strong>
                  {gmFinalOffer.deliveryDays !== null
                    ? `${gmFinalOffer.deliveryDays} gün`
                    : "—"}
                </strong>
              </div>
              <div>
                <span>Vade</span>
                <strong>
                  {gmFinalOffer.paymentDays !== null
                    ? `${gmFinalOffer.paymentDays} gün`
                    : "—"}
                </strong>
              </div>
            </div>

            <label className="revision-note-field">
              <span className="revision-note-heading">
                <span>Değişiklik gerekçesi</span>
                <small>
                  {gmFinalDecisionNote.trim().length}
                  /10+
                </small>
              </span>
              <textarea
                autoFocus
                rows={6}
                value={gmFinalDecisionNote}
                disabled={decisionSubmitting}
                aria-invalid={
                  gmFinalDecisionNote.trim().length >
                  0 &&
                  gmFinalDecisionNote.trim().length <
                  10
                }
                aria-describedby={`gm-override-help-${request.id}`}
                placeholder={`${selectedOffer?.supplier ?? "Müdür önerisi"} yerine ${gmFinalOffer.supplier} seçilme gerekçesini yazın.`}
                onChange={(event) => {
                  setGmFinalDecisionNotes(
                    (current) => ({
                      ...current,
                      [request.id]:
                        event.target.value,
                    }),
                  );
                  setDecisionError("");
                }}
              />
              <small
                id={`gm-override-help-${request.id}`}
                className={
                  gmFinalDecisionNote.trim().length >
                    0 &&
                    gmFinalDecisionNote.trim().length <
                    10
                    ? "is-error"
                    : ""
                }
                aria-live="polite"
              >
                {gmFinalDecisionNote.trim().length >
                  0 &&
                  gmFinalDecisionNote.trim().length < 10
                  ? `Onaylamak için ${10 - gmFinalDecisionNote.trim().length} karakter daha yazın.`
                  : "Farklı seçimin ticari veya operasyonel nedenini somut biçimde açıklayın."}
              </small>
            </label>

            {decisionError ? (
              <div
                className="decision-inline-error"
                role="alert"
              >
                <AlertTriangle
                  size={18}
                  aria-hidden="true"
                />
                <div>
                  <strong>
                    Nihai karar kaydedilemedi
                  </strong>
                  <span>{decisionError}</span>
                </div>
              </div>
            ) : null}
          </div>

          <footer className="revision-submit-bar">
            <div className="revision-submit-summary">
              <strong>{gmFinalOffer.supplier}</strong>
              <span>
                Müdür önerisinden farklı nihai seçim
              </span>
            </div>
            <div className="revision-submit-actions">
              <button
                type="button"
                className="secondary-button"
                disabled={decisionSubmitting}
                onClick={closeGmOverrideComposer}
              >
                Vazgeç
              </button>
              <button
                type="button"
                className="approve-button"
                disabled={
                  decisionSubmitting ||
                  gmFinalDecisionNote.trim().length <
                  10
                }
                onClick={() =>
                  act(
                    request,
                    "Onaylandı",
                    gmFinalDecisionNote,
                    gmFinalOffer.id,
                  )
                }
              >
                <Check size={16} />
                {decisionSubmitting
                  ? "Kaydediliyor…"
                  : "Seçimi değiştir ve nihai onayla"}
              </button>
            </div>
          </footer>
        </section>
      </div>
    ) : null}
  </>);
}
